import React, { useRef, startTransition } from "react";
import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import BookSparkGreenSvg from "../../assets/book_spark_purple_icon.svg";
import sendSvg from "../../assets/white_arrow_demo_icon.svg";
import BookSpark from "../../assets/book_spark.svg";
const SearchContainer = styled(Box)<{
  focused: boolean;
  hasValue: boolean;
  ishovered: boolean;
}>(({ theme, focused, hasValue, ishovered }) => ({
  display: "flex",
  alignItems: "center",
  borderRadius: "100px",
  padding: "8px 16px",
  transition: "all 0.3s ease",
  width: hasValue ? "calc(100% - 56px)" : "100%",
  height: "40px",
  border: `1px solid ${focused || hasValue ? "#3474E04D" : "#ABB1BA"}`,
  boxShadow: hasValue
    ? "0px 8px 24px 0px #0000001A, 0px 0px 0px 2px #3474E040"
    : "none",
  position: "relative",
  zIndex: 2,
  backgroundColor: "white",
  "&::before":
    focused || hasValue
      ? {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "100px",
          padding: "2px",
          background: "linear-gradient(90deg, #9B7AF7, #3244E8)",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        }
      : {},
}));

const BookIconWrapper = styled(Box)<{ ishovered: boolean }>(
  ({ ishovered }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    transition: "all 0.5s ease",
    transform: ishovered
      ? "rotate(360deg) scale(0.9)"
      : "rotate(0deg) scale(1)",
    repeat: ishovered ? "infinity" : "",
  })
);

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: "100%",
  "& .MuiInputBase-input": {
    padding: "4px 8px",
    fontSize: "16px",
    fontFamily: 'Google Sans',
    width: "100%",
  },
}));

const SendButton = styled(IconButton)<{ sideDrawer: boolean }>(
  ({ theme, sideDrawer }) => ({
    background: sideDrawer
      ? "radial-gradient(49.53% 50% at 50% 50%, #3244E8 0%, rgba(0, 2, 16, 0) 100%), linear-gradient(0deg, #9B7AF7, #9B7AF7)"
      : "linear-gradient(89.82deg, #9B7AF7 7.17%, #3244E8 139.18%)",
    width: "40px",
    height: "40px",
    marginLeft: "16px",
    position: "relative",
    overflow: "hidden",
    boxShadow: sideDrawer
      ? "-4px -4px 8px 0px #3FFFFF87 inset,0px 0px 4px 2px #FFFFFFCC inset "
      : "0px 0px 2px 1px #FFFFFFAA inset, -2px -2px 5px 0px #3FFFFF67 inset",
    "&:hover": {
      background: sideDrawer
        ? "radial-gradient(49.53% 50% at 50% 50%, #3244E8 0%, rgba(0, 2, 16, 0) 100%), linear-gradient(0deg, #9B7AF7, #9B7AF7)"
        : "linear-gradient(89.82deg, #9B7AF7 7.17%, #3244E8 139.18%)",
    },
    "&:active": {
      transform: "translateY(1px)",
      boxShadow: sideDrawer
        ? "-4px -4px 8px 0px #3FFFFF87 inset,0px 0px 4px 2px #FFFFFFCC inset "
        : "0px 0px 2px 1px #FFFFFFAA inset, -2px -2px 5px 0px #3FFFFF67 inset",
    },
  })
);

interface SearchInputProps {
  placeholder?: string;
  onSearch?: (value: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  sideDrawer?: boolean;
  disabled?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "Search or Ask Orion",
  onSearch,
  value = "",
  onChange,
  sideDrawer = false,
  disabled = false,
}) => {
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleSubmit = () => {
    startTransition(() => {
      if (onSearch && value.trim()) {
        onSearch(value);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && value.trim()) {
      handleSubmit();
    }
  };

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <SearchContainer
        focused={focused}
        hasValue={!!value.trim()}
        ishovered={hovered}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <BookIconWrapper ishovered={hovered}>
          {sideDrawer ? (
            <img
              src={BookSpark}
              alt=""
              style={{ width: "100%", height: "100%" }}
            />
          ) : (
            <img
              src={BookSparkGreenSvg}
              alt=""
              style={{ width: "100%", height: "100%" }}
            />
          )}
        </BookIconWrapper>
        <StyledInputBase
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          inputProps={{ "aria-label": "search" }}
          disabled={disabled}
        />
      </SearchContainer>

      {value.trim() && (
        <SendButton
          onClick={handleSubmit}
          aria-label="send"
          sideDrawer={sideDrawer}
          disabled={disabled}
          style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          <img src={sendSvg} alt="" style={{ width: "19px", height: "16px" }} />
        </SendButton>
      )}
    </Box>
  );
};

export default SearchInput;
